import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent
} from 'react';
import { createPortal } from 'react-dom';
import { Button } from './primitives/Button';
import { IconButton } from './primitives/IconButton';
import { Modal } from './primitives/Modal';
import { WysiwygEditor } from './primitives/WysiwygEditor';
import { useDocumentEditorState } from '../hooks/useDocumentEditorState';

export type AttachDocumentLifecycle = 'draft' | 'published' | 'archived';

export interface AttachDocumentRow {
  id: string;
  title: string;
  lifecycle: AttachDocumentLifecycle;
  ownerName: string | null;
  updatedAt: string;
  /** Optional preview body shown in the side-rail when the row is single-clicked. */
  bodyExcerpt?: string;
}

interface PickerProps {
  mode?: 'picker';
  open: boolean;
  onClose: () => void;
  onAttach: (docIds: string[]) => Promise<void>;
  workItemId: string;
  /**
   * Caller-supplied document list. The primitive is presentational —
   * the route layer owns the React Query / Supabase fetch.
   */
  documents: AttachDocumentRow[];
}

interface ViewerProps {
  mode: 'viewer';
  open: boolean;
  onClose: () => void;
  workItemId: string;
  /** Required when mode === 'viewer'. The document the cover loads + edits. */
  docId: string;
  /** Optional: title to show in the cover header before the document loads. */
  docTitle?: string;
}

export type AttachDocumentCoverProps = PickerProps | ViewerProps;

type SortKey = 'title' | 'lifecycle' | 'ownerName' | 'updatedAt';
type LifecycleFilter = 'all' | AttachDocumentLifecycle;

const LIFECYCLE_LABEL: Record<AttachDocumentLifecycle, string> = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived'
};

function compare(a: string | null, b: string | null): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  return a.localeCompare(b);
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

export function AttachDocumentCover(props: AttachDocumentCoverProps) {
  if (props.mode === 'viewer') {
    return <DocumentCoverViewer {...props} />;
  }
  return <DocumentCoverPicker {...props} />;
}

// -- Picker mode ----------------------------------------------------------

function DocumentCoverPicker({
  open,
  onClose,
  onAttach,
  workItemId,
  documents
}: PickerProps) {
  const [query, setQuery] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState<LifecycleFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('title');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [attaching, setAttaching] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setLifecycleFilter('all');
    setSelectedIds([]);
    setPreviewId(null);
    panelRef.current?.focus();
  }, [open]);

  const visibleRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = documents.filter((d) => {
      if (lifecycleFilter !== 'all' && d.lifecycle !== lifecycleFilter) return false;
      if (!needle) return true;
      return d.title.toLowerCase().includes(needle);
    });
    const sorted = [...filtered].sort((a, b) => {
      const av =
        sortKey === 'title'
          ? a.title
          : sortKey === 'lifecycle'
            ? LIFECYCLE_LABEL[a.lifecycle]
            : sortKey === 'ownerName'
              ? a.ownerName
              : a.updatedAt;
      const bv =
        sortKey === 'title'
          ? b.title
          : sortKey === 'lifecycle'
            ? LIFECYCLE_LABEL[b.lifecycle]
            : sortKey === 'ownerName'
              ? b.ownerName
              : b.updatedAt;
      const cmp = compare(av, bv);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [documents, query, lifecycleFilter, sortKey, sortDir]);

  const previewDoc = useMemo(
    () => (previewId ? documents.find((d) => d.id === previewId) ?? null : null),
    [previewId, documents]
  );

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const handleDone = async () => {
    if (selectedIds.length === 0 || attaching) return;
    setAttaching(true);
    try {
      await onAttach([...selectedIds]);
      onClose();
    } finally {
      setAttaching(false);
    }
  };

  const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
    }
  };

  if (!open) return null;

  const counter = `${selectedIds.length} document${selectedIds.length === 1 ? '' : 's'} selected`;

  return createPortal(
    <div
      className="pn-attach-cover-backdrop"
      data-testid="attach-document-cover-backdrop"
      data-mode="picker"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className="pn-attach-cover"
        role="dialog"
        aria-modal="true"
        aria-label="Attach Document"
        data-testid="attach-document-cover"
        data-mode="picker"
        data-work-item-id={workItemId}
        tabIndex={-1}
        onKeyDown={handleKey}
      >
        <header className="pn-attach-cover-head">
          <div className="pn-attach-cover-titlebar">
            <h2 className="pn-attach-cover-title">Attach Document</h2>
            <span
              className="pn-attach-cover-counter"
              data-testid="attach-document-cover-counter"
            >
              {counter}
            </span>
          </div>
          <div className="pn-attach-cover-actions">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              data-testid="attach-document-cover-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleDone}
              disabled={selectedIds.length === 0 || attaching}
              data-testid="attach-document-cover-done"
            >
              {attaching ? 'Attaching…' : 'Done'}
            </Button>
            <IconButton
              aria-label="Close attach document cover"
              data-testid="attach-document-cover-close"
              onClick={onClose}
            >
              ×
            </IconButton>
          </div>
        </header>

        <div className="pn-attach-cover-toolbar">
          <input
            type="search"
            className="pn-attach-cover-search"
            placeholder="Search documents…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="attach-document-cover-search"
            aria-label="Search documents"
          />
          <div
            className="pn-attach-cover-chips"
            role="radiogroup"
            aria-label="Filter by lifecycle"
          >
            {(['all', 'draft', 'published', 'archived'] as const).map((v) => (
              <button
                key={v}
                type="button"
                className="pn-attach-cover-chip"
                role="radio"
                aria-checked={lifecycleFilter === v}
                data-testid={`attach-document-cover-chip-${v}`}
                data-active={lifecycleFilter === v ? 'true' : 'false'}
                onClick={() => setLifecycleFilter(v)}
              >
                {v === 'all' ? 'All' : LIFECYCLE_LABEL[v]}
              </button>
            ))}
          </div>
        </div>

        <div className="pn-attach-cover-body">
          <div
            className="pn-attach-cover-list"
            data-testid="attach-document-cover-list"
            data-cols="5"
            data-row-count={visibleRows.length}
          >
            <div
              className="pn-attach-cover-list-head"
              role="row"
              aria-label="Document columns"
            >
              <span className="pn-attach-cover-checkbox-col" role="columnheader">
                <span className="pn-visually-hidden">Select</span>
              </span>
                {(
                  [
                    ['title', 'Title'],
                    ['lifecycle', 'Lifecycle'],
                    ['ownerName', 'Owner'],
                    ['updatedAt', 'Updated']
                  ] as Array<[SortKey, string]>
                ).map(([key, label]) => {
                  const sorted = sortKey === key;
                  return (
                    <span
                      key={key}
                      className="pn-attach-cover-list-head-cell"
                      role="columnheader"
                      data-testid={`attach-document-cover-header-${key}`}
                      data-sort={sorted ? sortDir : 'none'}
                    >
                      <button
                        type="button"
                        className="pn-attach-cover-header-btn"
                        onClick={() => toggleSort(key)}
                      >
                        <span>{label}</span>
                        <span aria-hidden="true">
                          {sorted ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                        </span>
                      </button>
                    </span>
                  );
                })}
            </div>
            <ul className="pn-attach-cover-rows" role="list">
              {visibleRows.length === 0 ? (
                <li
                  className="pn-attach-cover-empty"
                  data-testid="attach-document-cover-empty"
                  role="listitem"
                >
                  No documents match.
                </li>
              ) : (
                visibleRows.map((d) => {
                  const checked = selectedIds.includes(d.id);
                  return (
                    <li
                      key={d.id}
                      className="pn-attach-cover-row"
                      role="listitem"
                      data-testid={`attach-document-cover-row-${d.id}`}
                      data-selected={checked ? 'true' : 'false'}
                      data-previewing={previewId === d.id ? 'true' : 'false'}
                    >
                      <span className="pn-attach-cover-checkbox-col">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSelection(d.id)}
                          data-testid={`attach-document-cover-checkbox-${d.id}`}
                          aria-label={`Select ${d.title}`}
                        />
                      </span>
                      <span className="pn-attach-cover-row-title">
                        <button
                          type="button"
                          className="pn-attach-cover-title-btn"
                          onClick={() => setPreviewId(d.id)}
                          data-testid={`attach-document-cover-title-${d.id}`}
                        >
                          {d.title}
                        </button>
                      </span>
                      <span data-lifecycle={d.lifecycle}>
                        {LIFECYCLE_LABEL[d.lifecycle]}
                      </span>
                      <span>{d.ownerName ?? '—'}</span>
                      <span>{formatTimestamp(d.updatedAt)}</span>
                    </li>
                  );
                })
              )}
            </ul>
          </div>

          {previewDoc ? (
            <aside
              className="pn-attach-cover-preview"
              aria-label="Document preview"
              data-testid="attach-document-cover-preview"
            >
              <header>
                <h3 data-testid="attach-document-cover-preview-title">
                  {previewDoc.title}
                </h3>
                <IconButton
                  aria-label="Close preview"
                  data-testid="attach-document-cover-preview-close"
                  onClick={() => setPreviewId(null)}
                >
                  ×
                </IconButton>
              </header>
              <p className="pn-attach-cover-preview-meta">
                {LIFECYCLE_LABEL[previewDoc.lifecycle]} ·{' '}
                {previewDoc.ownerName ?? 'No owner'} ·{' '}
                {formatTimestamp(previewDoc.updatedAt)}
              </p>
              <div className="pn-attach-cover-preview-body">
                {previewDoc.bodyExcerpt ?? 'No preview available.'}
              </div>
            </aside>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}

// -- Viewer mode ----------------------------------------------------------

type PendingExit =
  | null
  | { kind: 'close' }
  | { kind: 'open-in-editor'; href: string };

function DocumentCoverViewer({
  open,
  onClose,
  workItemId,
  docId,
  docTitle
}: ViewerProps) {
  const [toast, setToast] = useState<string | null>(null);
  const editor = useDocumentEditorState({
    documentId: open ? docId : undefined,
    onError: setToast
  });
  const {
    load,
    titleDraft,
    setTitleDraft,
    bodyDraft,
    setBodyDraft,
    saveState,
    dirty,
    onSave,
    resetDrafts
  } = editor;
  const panelRef = useRef<HTMLDivElement | null>(null);
  const editorContainerRef = useRef<HTMLDivElement | null>(null);
  const [pendingExit, setPendingExit] = useState<PendingExit>(null);
  const [modalSavePending, setModalSavePending] = useState(false);
  // Focus the editor surface on open so the first keystroke isn't lost on
  // the panel div.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const target = editorContainerRef.current?.querySelector<HTMLElement>(
        '[contenteditable="true"]'
      );
      if (target) target.focus();
      else panelRef.current?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [open, load.kind]);

  const requestExit = useCallback(
    (action: NonNullable<PendingExit>) => {
      if (dirty) {
        setPendingExit(action);
        return;
      }
      runExit(action);
    },
    [dirty]
  );

  const runExit = useCallback(
    (action: NonNullable<PendingExit>) => {
      if (action.kind === 'open-in-editor') {
        window.open(action.href, '_blank', 'noopener,noreferrer');
      }
      onClose();
    },
    [onClose]
  );

  // After a SAVE driven by the modal lands cleanly, complete the pending
  // exit. On conflict/failed, the modal closes and the save-state banner
  // stays visible inside the cover for the user to decide.
  useEffect(() => {
    if (!pendingExit) return;
    if (saveState === 'saved' && !dirty) {
      const target = pendingExit;
      setPendingExit(null);
      runExit(target);
    } else if (saveState === 'conflict' || saveState === 'failed') {
      setPendingExit(null);
    }
  }, [saveState, dirty, pendingExit, runExit]);

  const handleHeaderCancel = useCallback(() => {
    requestExit({ kind: 'close' });
  }, [requestExit]);

  const handleOpenInEditor = useCallback(
    (e: ReactMouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      const href = `/document-editor/${docId}`;
      requestExit({ kind: 'open-in-editor', href });
    },
    [requestExit, docId]
  );

  const handleKey = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Escape') return;
      // Defer Esc to editor slash menu if focus
      // is inside the editor container — let those close first. The cover's
      // Esc only fires when the editor isn't holding the key.
      const active = document.activeElement;
      const insideEditor = editorContainerRef.current?.contains(active ?? null);
      if (insideEditor && active && active !== editorContainerRef.current) {
        // Yield: child handler runs without our stopPropagation interference.
        return;
      }
      e.stopPropagation();
      requestExit({ kind: 'close' });
    },
    [requestExit]
  );

  const handleScrimMouseDown = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      // Suppress scrim-close while a text selection is in progress (a
      // drag-select inside the editor that releases on the backdrop would
      // otherwise dismiss the cover).
      const sel = window.getSelection();
      if (sel && sel.toString().length > 0) return;
      requestExit({ kind: 'close' });
    },
    [requestExit]
  );

  const onModalSaveAndContinue = useCallback(async () => {
    setModalSavePending(true);
    try {
      await onSave();
    } finally {
      setModalSavePending(false);
    }
  }, [onSave]);

  const onModalDiscard = useCallback(() => {
    if (!pendingExit) return;
    const target = pendingExit;
    setPendingExit(null);
    resetDrafts();
    // resetDrafts flips dirty to false next render; fire the exit on the
    // next tick so the dirty-check there is consistent.
    setTimeout(() => runExit(target), 0);
  }, [pendingExit, resetDrafts, runExit]);

  const onModalCancel = useCallback(() => {
    setPendingExit(null);
  }, []);

  if (!open) return null;

  const headerTitle =
    load.kind === 'ready'
      ? titleDraft || 'Untitled'
      : docTitle || 'Loading…';

  return createPortal(
    <div
      className="pn-attach-cover-backdrop"
      data-testid="attach-document-cover-backdrop"
      data-mode="viewer"
      onMouseDown={handleScrimMouseDown}
    >
      <div
        ref={panelRef}
        className="pn-attach-cover"
        role="dialog"
        aria-modal="true"
        aria-label={headerTitle}
        data-testid="attach-document-cover"
        data-mode="viewer"
        data-work-item-id={workItemId}
        data-doc-id={docId}
        tabIndex={-1}
        onKeyDown={handleKey}
      >
        <header className="pn-attach-cover-head" data-mode="viewer">
          <div className="pn-attach-cover-titlebar">
            <h2 className="pn-attach-cover-title" data-testid="document-cover-title">
              {headerTitle}
            </h2>
          </div>
          <div className="pn-attach-cover-actions">
            <a
              className="pn-attach-cover-open-link"
              href={`/document-editor/${docId}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="document-cover-open-in-editor"
              onClick={handleOpenInEditor}
            >
              Open in editor
            </a>
            <Button
              type="button"
              variant="ghost"
              onClick={handleHeaderCancel}
              data-testid="document-cover-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => void onSave()}
              disabled={!dirty || saveState === 'saving'}
              data-testid="document-cover-save"
              data-state={saveState}
            >
              {saveLabel(saveState, dirty)}
            </Button>
            <IconButton
              aria-label="Close document cover"
              data-testid="document-cover-close"
              onClick={handleHeaderCancel}
            >
              ×
            </IconButton>
          </div>
        </header>

        <div className="pn-attach-cover-viewer-body" data-mode="viewer">
          {load.kind === 'loading' ? (
            <div
              className="pn-attach-cover-viewer-loading"
              data-testid="document-cover-loading"
            >
              Loading document…
            </div>
          ) : load.kind === 'notfound' ? (
            <div
              className="pn-attach-cover-viewer-empty"
              data-testid="document-cover-notfound"
            >
              Document not found.
            </div>
          ) : load.kind === 'error' ? (
            <div
              className="pn-attach-cover-viewer-empty"
              data-testid="document-cover-error"
            >
              {load.message}
            </div>
          ) : (
            <>
              <input
                type="text"
                className="pn-attach-cover-viewer-title"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                data-testid="document-cover-title-input"
                aria-label="Document title"
              />
              <div ref={editorContainerRef} className="pn-attach-cover-viewer-editor">
                <WysiwygEditor
                  value={bodyDraft}
                  onChange={setBodyDraft}
                  onSave={() => void onSave()}
                  saveState={saveState}
                  hideInlineSave
                  dataTestId="document-cover-body"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {pendingExit ? (
        <Modal
          open
          onClose={onModalCancel}
          title="You have unsaved changes"
          dataTestId="document-cover-unsaved-modal"
          footer={
            <div className="pn-attach-cover-modal-actions">
              <Button
                variant="ghost"
                size="sm"
                onClick={onModalCancel}
                data-testid="document-cover-unsaved-cancel"
              >
                Keep editing
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onModalDiscard}
                data-testid="document-cover-unsaved-discard"
              >
                Discard
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void onModalSaveAndContinue()}
                disabled={modalSavePending || saveState === 'saving'}
                data-testid="document-cover-unsaved-save"
              >
                {modalSavePending ? 'Saving…' : 'Save and continue'}
              </Button>
            </div>
          }
        >
          <p>
            Save your changes before leaving, or discard them to continue.
          </p>
        </Modal>
      ) : null}

      {toast ? (
        <div
          className="pn-attach-cover-toast"
          data-testid="document-cover-toast"
          role="status"
        >
          {toast}
        </div>
      ) : null}
    </div>,
    document.body
  );
}

function saveLabel(state: string, dirty: boolean): string {
  switch (state) {
    case 'saving':
      return 'Saving…';
    case 'saved':
      return dirty ? 'Save' : 'Saved';
    case 'conflict':
      return 'Save';
    case 'failed':
      return 'Retry save';
    default:
      return 'Save';
  }
}
